import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Loader2, ShieldCheck, Timer, Shuffle, EyeOff, Lock, Maximize2 } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { isStudent } from "@/lib/classroom_live";
import { AIOT_PATH, PORTAL, errText, loginUrl, testApi } from "@/lib/aiot";
import { TestRunner } from "@/components/aiot/test/TestRunner";
import { TestResult } from "@/components/aiot/test/TestResult";

const Intro = ({ st, onStart, busy, err }) => {
  const { L } = useLang();
  const rules = [[Timer, L(`${st.minutes} minutes, the timer keeps running even if you close the page`, `${st.minutes} मिनट, पेज बंद करने पर भी टाइमर चलता रहता है`)],
    [Lock, L("One attempt only. Once started, it counts", "सिर्फ़ एक प्रयास। शुरू करते ही गिना जाएगा")],
    [Shuffle, L("Questions, options and 3D tasks are randomised for you and checked on the server", "सवाल, विकल्प और 3D टास्क आपके लिए रैंडम हैं और सर्वर पर जाँचे जाते हैं")],
    [EyeOff, L(`Leaving the test tab or full screen is logged. ${st.max_focus} times submits your test`, `टेस्ट टैब या फ़ुल स्क्रीन छोड़ना दर्ज होता है। ${st.max_focus} बार पर टेस्ट सबमिट हो जाएगा`)],
    [Maximize2, L("The test opens in full screen", "टेस्ट फ़ुल स्क्रीन में खुलेगा")]];
  return (
    <div className="max-w-2xl mx-auto rounded-3xl border border-slate-200 bg-white p-6 sm:p-10" data-testid="test-intro">
      <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#0F766E]">{L(`Week ${st.week} test`, `हफ़्ता ${st.week} टेस्ट`)}</div>
      <h1 className="mt-2 text-2xl sm:text-3xl font-extrabold text-[#0B1F3A]" data-testid="test-intro-title">{L(st.title, st.title_hi)}</h1>
      <ul className="mt-6 space-y-3">{rules.map(([I, t]) => <li key={t} className="flex gap-3 text-sm text-slate-700"><I className="w-5 h-5 shrink-0 text-[#0F766E]" />{t}</li>)}</ul>
      {err && <p className="mt-5 text-sm font-semibold text-[#DC2626]" data-testid="test-intro-error">{err}</p>}
      <button type="button" onClick={onStart} disabled={busy} className="mt-8 w-full h-12 rounded-full bg-[#0F766E] text-white font-bold inline-flex items-center justify-center gap-2 hover:bg-[#0B1F3A] disabled:opacity-60" data-testid="test-start">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}{L("I understand, start the test", "मैं समझ गया/गई, टेस्ट शुरू करें")}
      </button>
    </div>
  );
};

const Notice = ({ children, testid }) => {
  const { L } = useLang();
  return (
    <div className="max-w-xl mx-auto rounded-3xl border border-slate-200 bg-white p-10 text-center" data-testid={testid}>
      {children}
      <Link to={`${PORTAL}?view=tests`} className="mt-6 inline-flex rounded-full bg-[#0B1F3A] text-white px-5 h-11 items-center font-bold" data-testid="test-back-portal">{L("Back to weekly tests", "साप्ताहिक टेस्ट पर वापस")}</Link>
    </div>
  );
};

export default function AiotTest() {
  const { week } = useParams();
  const w = Number(week);
  const { L } = useLang();
  const nav = useNavigate();
  const [st, setSt] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isStudent()) { nav(loginUrl(`${AIOT_PATH}/test/${w}`), { replace: true }); return; }
    testApi.status(w).then(setSt).catch((e) => setErr(errText(e, "Could not load the test.")));
  }, [w, nav]);

  const start = async () => {
    setBusy(true); setErr("");
    try {
      await Promise.resolve(document.documentElement.requestFullscreen?.()).catch(() => {});
      const att = await testApi.start(w);
      setSt({ ...st, ...att });
    } catch (e) { setErr(errText(e, "Could not start the test.")); } finally { setBusy(false); }
  };
  const done = useCallback((result) => setSt((s) => ({ ...s, state: "done", result })), []);

  const title = st && L(st.title, st.title_hi);
  let body;
  if (!st && err) body = <Notice testid="test-error"><p className="text-sm font-semibold text-[#DC2626]">{err}</p></Notice>;
  else if (!st) body = <div className="h-80 grid place-items-center"><Loader2 className="w-7 h-7 animate-spin text-[#0F766E]" /></div>;
  else if (st.state === "done") body = <TestResult week={w} title={title} result={st.result} />;
  else if (st.state === "in_progress") body = <TestRunner week={w} title={title} att={st} onDone={done} />;
  else if (st.state === "open") body = <Intro st={st} onStart={start} busy={busy} err={err} />;
  else body = <Notice testid={`test-${st.state}`}><h1 className="text-xl font-extrabold text-[#0B1F3A]">{title}</h1><p className="mt-2 text-sm text-slate-600">{st.state === "locked" ? L(`This test opens on ${st.opens}.`, `यह टेस्ट ${st.opens} को खुलेगा।`) : L("The window for this test has closed. Missed tests count as 0%.", "इस टेस्ट का समय ख़त्म हो गया। छूटे टेस्ट 0% गिने जाते हैं।")}</p></Notice>;

  return <div className="bg-[#F3F8FB] min-h-screen px-4 py-6 overflow-y-auto" data-testid="aiot-test-page">{body}</div>;
}
